import React, { useEffect, useState } from "react";
import Select, { createFilter } from "react-select";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Icon,
  PageHeader,
  SegmentedControl,
  Select as AdminSelect,
  Table,
  TextField,
  styles as ui,
} from "stories/Admin";
import type { Column } from "stories/Admin";
import useFetch from "hooks/useFetch";
import { adminSelectStyles } from "../adminSelectStyles";
import { generateRamadanPDF } from "./ramadanPdfExport";
import styles from "./MawaqeetRamadanPage.module.scss";

interface PrayerTimes {
  date: string;
  fajr: string;
  fajr_endtime: string;
  duhr: string;
  asr: string;
  maghrib: string;
  isha: string;
  asr_1x_shadow: string;
  asr_2x_shadow: string;
  hijriDate: string;
}

interface PrayerTimeTable {
  id: number;
  name: string;
}

interface Location {
  id: number;
  name: string;
  prayerTimesTables: PrayerTimeTable[];
}

export interface Mosque {
  id: number;
  logo: string;
  orgNr: string;
  name: string;
  orgName: string;
  locationId: number;
  locationName: string;
  prayerTimeTable: number;
  prayerTimeTableName: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  contactPhone: string;
  contactEmail: string;
}

export interface RamadanDay {
  day: number; // Ramadan day 1-30
  hijriYear: number; // Hijri year (e.g., 1446)
  gregorianDate: string; // "YYYY-MM-DD"
  formattedDate: string; // "DD.MMM"
  weekday: string;
  weekdayFull: string;
  isFriday: boolean;
  suhur: string; // Fajr - 5 minutes
  fajr: string;
  iftar: string; // Maghrib
  isha: string;
}

const WEEKDAY_NO: Record<number, string> = {
  0: "Søn",
  1: "Man",
  2: "Tir",
  3: "Ons",
  4: "Tor",
  5: "Fre",
  6: "Lør",
};

const WEEKDAY_FULL_NO: Record<number, string> = {
  0: "Søndag",
  1: "Mandag",
  2: "Tirsdag",
  3: "Onsdag",
  4: "Torsdag",
  5: "Fredag",
  6: "Lørdag",
};

const MONTH_NAMES_NO: Record<number, string> = {
  0: "jan",
  1: "feb",
  2: "mar",
  3: "apr",
  4: "mai",
  5: "jun",
  6: "jul",
  7: "aug",
  8: "sep",
  9: "okt",
  10: "nov",
  11: "des",
};

type SourceType = "city" | "mosque";

/**
 * Trekker fra minutter fra en tid-streng (HH:MM)
 */
function subtractMinutes(timeStr: string, minutes: number): string {
  const [hours, mins] = timeStr.split(":").map(Number);
  const totalMins = hours * 60 + mins - minutes;
  const newHours = Math.floor(totalMins / 60) % 24;
  const newMins = totalMins % 60;
  return `${String(newHours).padStart(2, "0")}:${String(newMins < 0 ? 60 + newMins : newMins).padStart(2, "0")}`;
}

const MawaqeetRamadanPage: React.FC = () => {
  // Source toggle
  const [sourceType, setSourceType] = useState<SourceType>("city");

  // City state
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null,
  );

  // Mosque state
  const [mosques, setMosques] = useState<Mosque[]>([]);
  const [selectedMosqueOrgNr, setSelectedMosqueOrgNr] = useState<string | null>(
    null,
  );

  // Common state
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [ramadanDays, setRamadanDays] = useState<RamadanDay[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("");
  const [customMessage, setCustomMessage] = useState<string>(""); // Egendefinert melding for PDF

  const defaultTableId = 2; // Mawaqeet table

  const { execute: fetchLocations } = useFetch<Location[]>();
  const { execute: fetchMosques } = useFetch<Mosque[]>();
  const { execute: fetchPrayerTimes } = useFetch<PrayerTimes[]>();

  // Fetch locations for dropdown
  useEffect(() => {
    const fetchLocationData = async () => {
      const result = await fetchLocations("/api/v1/locations", "GET");
      if (result) {
        const filteredLocations = result
          .filter((location) =>
            location.prayerTimesTables.some((table) => table.id === 2),
          )
          .sort((a, b) => a.name.localeCompare(b.name));

        setLocations(filteredLocations);
      }
    };
    fetchLocationData();
  }, []);

  // Fetch mosques for dropdown
  useEffect(() => {
    const fetchMosqueData = async () => {
      const result = await fetchMosques("/api/v1/mosques", "GET");
      if (result) {
        const sortedMosques = result.sort((a, b) =>
          (a.name || a.orgName).localeCompare(b.name || b.orgName),
        );
        setMosques(sortedMosques);
      }
    };
    fetchMosqueData();
  }, []);

  const selectedLocation =
    locations.find((l) => l.id === selectedLocationId) || null;
  const selectedMosque =
    mosques.find((m) => m.orgNr === selectedMosqueOrgNr) || null;

  const getDisplayName = (): string => {
    if (sourceType === "mosque" && selectedMosque) {
      return selectedMosque.name || selectedMosque.orgName;
    }
    if (sourceType === "city" && selectedLocation) {
      return selectedLocation.name;
    }
    return "";
  };

  const findRamadanDays = async () => {
    let locationId: number | null = null;
    let tableId: number = defaultTableId;

    if (sourceType === "city") {
      if (!selectedLocationId) {
        setError("Velg en by først.");
        return;
      }
      locationId = selectedLocationId;
    } else {
      if (!selectedMosque) {
        setError("Velg en moské først.");
        return;
      }
      locationId = selectedMosque.locationId;
      tableId = selectedMosque.prayerTimeTable || defaultTableId;
    }

    setIsLoading(true);
    setError(null);
    setStatus("Henter bønnetider for hele året …");
    setRamadanDays([]);

    try {
      // Fetch prayer times for the entire year
      const endpoint = `/api/v1/prayerTimes/${locationId}/${tableId}/${selectedYear}`;
      const result = await fetchPrayerTimes(endpoint, "GET");

      if (!result || result.length === 0) {
        setError("Ingen bønnetider funnet for valgt år og lokasjon.");
        setIsLoading(false);
        return;
      }

      setStatus("Søker etter Ramadan-dager ut fra hijri-datoen …");

      // Find Ramadan days (Hijri month = 9)
      // hijriDate format is "D-M-YYYY" (e.g., "1-9-1447" for 1 Ramadan 1447)
      const ramadanRows: RamadanDay[] = [];

      for (const prayer of result) {
        if (!prayer.hijriDate) continue;

        // Parse hijri date: "D-M-YYYY"
        const hijriParts = prayer.hijriDate.split("-");
        if (hijriParts.length !== 3) continue;

        const hijriDay = parseInt(hijriParts[0], 10);
        const hijriMonth = parseInt(hijriParts[1], 10);
        const hijriYear = parseInt(hijriParts[2], 10);

        // Check if it's Ramadan (month 9)
        if (hijriMonth === 9) {
          // Parse gregorian date
          const gregDate = new Date(prayer.date);
          const dayOfWeek = gregDate.getDay();
          const dayOfMonth = gregDate.getDate();
          const month = gregDate.getMonth();

          // Calculate Suhur (Fajr - 5 minutes)
          const suhur = subtractMinutes(prayer.fajr, 5);

          ramadanRows.push({
            day: hijriDay,
            hijriYear: hijriYear,
            gregorianDate: prayer.date,
            formattedDate: `${String(dayOfMonth).padStart(2, "0")}.${MONTH_NAMES_NO[month]}`,
            weekday: WEEKDAY_NO[dayOfWeek],
            weekdayFull: WEEKDAY_FULL_NO[dayOfWeek],
            isFriday: dayOfWeek === 5,
            suhur: suhur,
            fajr: prayer.fajr,
            iftar: prayer.maghrib,
            isha: prayer.isha,
          });
        }
      }

      if (ramadanRows.length === 0) {
        setError(
          "Fant ingen Ramadan-dager i valgt år. Sjekk at bønnetidene har hijri-datoer.",
        );
        setIsLoading(false);
        return;
      }

      // Sort by Ramadan day
      ramadanRows.sort((a, b) => a.day - b.day);

      setRamadanDays(ramadanRows);
      const displayName = getDisplayName();
      setStatus(
        `Fant ${ramadanRows.length} Ramadan-dager for ${displayName} ${selectedYear}.`,
      );
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Noe gikk galt under henting av data.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!ramadanDays.length) return;
    const displayName = getDisplayName();
    const isMosque = sourceType === "mosque";

    // For mosque, pass full mosque info
    const mosqueInfo = isMosque ? selectedMosque : undefined;

    generateRamadanPDF(
      ramadanDays,
      displayName,
      selectedYear,
      isMosque,
      mosqueInfo,
      customMessage.trim(),
    );
  };

  const locationOptions = locations.map((location) => ({
    value: location.id,
    label: location.name,
  }));

  const mosqueOptions = mosques.map((mosque) => ({
    value: mosque.orgNr,
    label: (mosque.name || mosque.orgName).toUpperCase(),
    logo: mosque.logo,
  }));

  const customFilter = createFilter({
    ignoreCase: true,
    ignoreAccents: true,
    matchFrom: "any",
    stringify: (option) => option.label,
  });

  const canGenerate =
    sourceType === "city"
      ? selectedLocationId !== null
      : selectedMosqueOrgNr !== null;

  const changeSource = (next: SourceType) => {
    setSourceType(next);
    setRamadanDays([]);
    setStatus("");
    setError(null);
  };

  const displayName = getDisplayName();
  const hijriYear = ramadanDays[0]?.hijriYear;

  const columns: Array<Column<RamadanDay>> = [
    {
      key: "day",
      header: "Ramadan",
      numeric: true,
      render: (d) => <b>{d.day}</b>,
    },
    { key: "weekday", header: "Dag", render: (d) => d.weekdayFull },
    {
      key: "date",
      header: "Dato",
      numeric: true,
      render: (d) => d.formattedDate,
    },
    {
      key: "suhur",
      header: "Suhur slutter",
      numeric: true,
      render: (d) => d.suhur,
    },
    { key: "fajr", header: "Fajr", numeric: true, render: (d) => d.fajr },
    { key: "iftar", header: "Iftar", numeric: true, render: (d) => d.iftar },
  ];

  return (
    <>
      <PageHeader
        title="Ramadan-kalender"
        subtitle="Lag en PDF med Suhur, Fajr og Iftar for en by eller moské."
      />

      <div className={styles.layout}>
        <div className={styles.column}>
          <Card title="Innstillinger">
            <div className={ui.fld}>
              <span className={ui.lbl}>Kalender for</span>
              <SegmentedControl
                label="Kalender for"
                value={sourceType}
                onChange={changeSource}
                options={[
                  { value: "city", label: "By" },
                  { value: "mosque", label: "Moské" },
                ]}
              />
            </div>

            {sourceType === "city" ? (
              <div className={ui.fld}>
                <label htmlFor="locationSearch" className={ui.lbl}>
                  By
                </label>
                <Select
                  inputId="locationSearch"
                  options={locationOptions}
                  onChange={(selectedOption) =>
                    setSelectedLocationId(selectedOption?.value || null)
                  }
                  value={
                    selectedLocationId
                      ? locationOptions.find(
                          (opt) => opt.value === selectedLocationId,
                        )
                      : null
                  }
                  placeholder="Søk og velg by"
                  filterOption={customFilter}
                  noOptionsMessage={() => "Ingen byer funnet"}
                  isClearable
                  styles={adminSelectStyles<{ value: number; label: string }>()}
                  menuPortalTarget={document.body}
                />
                <span className={ui.help}>
                  Bare byer med Mawaqeet-tabellen.
                </span>
              </div>
            ) : (
              <div className={ui.fld}>
                <label htmlFor="mosqueSearch" className={ui.lbl}>
                  Moské
                </label>
                <Select
                  inputId="mosqueSearch"
                  options={mosqueOptions}
                  onChange={(selectedOption) =>
                    setSelectedMosqueOrgNr(selectedOption?.value || null)
                  }
                  value={
                    selectedMosqueOrgNr
                      ? mosqueOptions.find(
                          (opt) => opt.value === selectedMosqueOrgNr,
                        )
                      : null
                  }
                  placeholder="Søk og velg moské"
                  filterOption={customFilter}
                  noOptionsMessage={() => "Ingen moskeer funnet"}
                  isClearable
                  styles={adminSelectStyles<{
                    value: string;
                    label: string;
                    logo: string;
                  }>()}
                  menuPortalTarget={document.body}
                  formatOptionLabel={(option) => (
                    <div className={styles.mosqueOption}>
                      {option.logo && (
                        <img
                          src={option.logo}
                          alt=""
                          className={styles.mosqueLogo}
                        />
                      )}
                      <span>{option.label}</span>
                    </div>
                  )}
                />
              </div>
            )}

            <AdminSelect
              id="yearSelect"
              label="År"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {Array.from({ length: 7 }, (_, index) => {
                const year = new Date().getFullYear() - 1 + index;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </AdminSelect>

            <TextField
              id="customMessage"
              label="Egendefinert melding (valgfri)"
              placeholder="F.eks. «Tarawih kl. 21:30 hver dag»"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              help="Vises i en egen boks over logoen i PDF-en. La stå tom for å skjule boksen."
            />

            <Button
              block
              onClick={findRamadanDays}
              disabled={isLoading || !canGenerate}
              loading={isLoading}
              icon={<Icon name="moon" size={16} strokeWidth={2} />}
            >
              {isLoading ? "Genererer …" : "Generer Ramadan-kalender"}
            </Button>
          </Card>

          <Card title="PDF" className={styles.pdfCard}>
            <div
              className={`${styles.sheet} ${sourceType === "mosque" ? styles.sheetGreen : ""}`}
              aria-hidden="true"
            >
              <div className={styles.sheetLeft}>
                <b className={styles.sheetYear}>{selectedYear}</b>
                {hijriYear && <span>{hijriYear} H</span>}
                <i className={styles.sheetTitle}>Ramadan Kalender</i>
                <b className={styles.sheetCity}>
                  {(
                    displayName || (sourceType === "mosque" ? "Moské" : "By")
                  ).toUpperCase()}
                </b>
                <div className={styles.sheetRows}>
                  {Array.from({ length: 21 }, (_, i) => (
                    <span
                      key={i}
                      className={
                        i === 0
                          ? styles.sheetHead
                          : i % 7 === 5
                            ? styles.sheetFri
                            : ""
                      }
                    />
                  ))}
                </div>
              </div>
              <div className={styles.sheetRight}>
                <span className={styles.sheetBox} />
                <span className={styles.sheetApp}>Last ned iBønn</span>
                <span className={styles.sheetFitr}>
                  HUSK Å GI SADAQAT-UL-FITR
                </span>
                {customMessage.trim() && (
                  <span className={styles.sheetMsg}>
                    {customMessage.trim()}
                  </span>
                )}
                <span className={styles.sheetLogo}>logo</span>
              </div>
            </div>
            <span className={ui.help}>
              Moské: grønt tema, moskeens logo, adresse og telefon. By:
              gull-tema og Mawaqeet-logo.
            </span>
          </Card>
        </div>

        <div className={styles.column}>
          {status && <Alert tone="info">{status}</Alert>}
          {error && <Alert tone="error">{error}</Alert>}

          <Card
            title={
              ramadanDays.length > 0
                ? `Ramadan ${selectedYear} – ${displayName}`
                : `Ramadan ${selectedYear}`
            }
            actions={
              ramadanDays.length > 0 && (
                <Button
                  size="sm"
                  onClick={handleDownloadPDF}
                  icon={<Icon name="download" size={16} strokeWidth={2} />}
                >
                  Last ned PDF
                </Button>
              )
            }
          >
            {ramadanDays.length > 0 ? (
              <>
                <Table
                  columns={columns}
                  rows={ramadanDays}
                  rowKey={(d) => d.day}
                  rowState={(d) => (d.isFriday ? "on" : undefined)}
                  caption="Ramadan-kalender"
                />
                <span className={`${ui.help} ${styles.center}`}>
                  Fredager er uthevet
                </span>
              </>
            ) : (
              <EmptyState icon="moon" title="Ingen kalender ennå">
                Velg by eller moské og år, og trykk «Generer Ramadan-kalender».
              </EmptyState>
            )}
          </Card>
        </div>
      </div>

      <Alert tone="info">
        Suhur slutter 5 minutter før Fajr. Iftar er Maghrib. Ramadan-dagene
        finnes ut fra hijri-datoen.
      </Alert>
    </>
  );
};

export default MawaqeetRamadanPage;
